<template>
  <header class="header">
    <!-- Bouton menu -->
    <button class="menu-btn" @click="toggleMobileMenu">
      <span class="material-icons">menu</span>
    </button>

    <div class="header-left">
      <h1 class="page-title">
        <slot name="icon"></slot>
        <span class="title-text">{{ title }}</span>
      </h1>
    </div>

    <div class="header-center">
      <!-- Slot pour les actions personnalisées -->
      <slot name="actions">
        <div class="search-box" :class="{ 'search-expanded': isSearchExpanded }">
          <button class="search-toggle" @click="toggleSearch" v-show="isMobile">
            <span class="material-icons">search</span>
          </button>
          <div class="search-input-container" :class="{ 'mobile-hidden': isMobile && !isSearchExpanded }">
            <span class="material-icons search-icon">search</span>
            <input 
              ref="searchInput"
              type="text" 
              :placeholder="isMobile ? 'Rechercher...' : 'Rechercher un agent...'" 
              class="search-input" 
              v-model="searchQuery" 
              @input="handleSearch" 
              @focus="showSearchResults = true"
              @blur="hideResultsDelayed"
            />
            <button 
              v-show="searchQuery && isMobile" 
              class="search-clear"
              @click="clearSearch"
            >
              <span class="material-icons">close</span>
            </button>
          </div>
        
          <!-- Résultats de recherche -->
          <div v-show="showSearchResults && (searchResults.length > 0 || isSearching)" class="search-results">
            <!-- Indicateur de chargement -->
            <div v-if="isSearching" class="search-loading">
              <div class="loading-spinner"></div>
              <span>Recherche en cours...</span>
            </div>
            
            <!-- Message si aucun résultat -->
            <div v-else-if="searchQuery.length >= 2 && searchResults.length === 0" class="search-no-results">
              <span class="material-icons">search_off</span>
              <span>Aucun agent trouvé</span>
            </div>
            
            <!-- Liste des résultats -->
            <div 
              v-else
              v-for="agent in searchResults" 
              :key="agent.id" 
              class="search-result-item"
              @mousedown="goToAgentProfile(agent.num_personnel)"
            >
              <div class="result-avatar">{{ agent.nom?.charAt(0).toUpperCase() }}</div>
              <div class="result-info">
                <div class="result-name">{{ agent.nom }} {{ agent.prenom }}</div>
                <div class="result-details">
                  <span v-if="agent.grade">{{ agent.grade }}</span>
                  <span v-if="agent.fonction">{{ agent.grade && agent.fonction ? ' • ' : ''}}{{ agent.fonction }}</span>
                  <span v-if="agent.matricule">{{ (agent.grade || agent.fonction) ? ' • ' : '' }}Matricule: {{ agent.matricule }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </slot>
    </div>

    <div class="header-right">
      <div class="header-actions">
        <button class="notification-bell" @click="toggleNotifications">
          <span class="material-icons">notifications</span>
          <span class="badge" v-if="notificationCount > 0">{{ notificationCount }}</span>
        </button>
        
        <div class="user-info" @click="toggleUserMenu" ref="userMenuRef">
          <span class="avatar">{{ userInitial }}</span>
          <div class="user-details" :class="{ 'mobile-hidden': isMobile }">
            <span class="user-name">{{ userName }}</span>
            <span class="user-role">{{ userRole }}</span>
          </div>
          <span class="material-icons dropdown-icon">arrow_drop_down</span>
          
          <!-- User dropdown menu -->
          <div class="user-dropdown" v-if="showUserMenu">
            <div class="dropdown-header">
              <div class="dropdown-avatar">{{ userInitial }}</div>
              <div class="dropdown-user-info">
                <div class="dropdown-name">{{ userName }}</div>
                <div class="dropdown-role">{{ userRole }}</div>
              </div>
            </div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-item" @click.stop="goToProfile">
              <span class="material-icons">person</span>
              <span>Mon Profil</span>
            </div>
            <div class="dropdown-item" @click.stop="goToChangePassword">
              <span class="material-icons">lock</span>
              <span>Changer mot de passe</span>
            </div>
            <div class="dropdown-item" @click.stop="openLogoutConfirmation">
              <span class="material-icons">logout</span>
              <span>Déconnexion</span>
            </div>
          </div>
        </div>
      </div>
    </div>
        
    <!-- Modal for logout confirmation -->
    <div class="modal-overlay" v-if="showLogoutModal" @click="cancelLogout">
      <div class="confirmation-modal" @click.stop>
        <div class="modal-header">
          <span class="material-icons warning-icon">warning</span>
          <h3>Confirmation de déconnexion</h3>
        </div>
        
        <div class="confirmation-content">
          <p>Êtes-vous sûr de vouloir vous déconnecter de votre session ?</p>
        </div>
        
        <div class="modal-actions">
          <button class="cancel-btn" @click="cancelLogout">
            <span class="material-icons">close</span>
            Annuler
          </button>
          <button class="logout-btn" @click="confirmLogout">
            <span class="material-icons">logout</span>
            Confirmer
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import api from '@/services/api'

const props = defineProps({
  title: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['toggle-sidebar'])

const router = useRouter()
const authStore = useAuthStore()
const userMenuRef = ref(null)
const searchInput = ref(null)

// État pour la responsivité
const isMobile = ref(false)
const isTablet = ref(false)
const isSearchExpanded = ref(false)

// État pour les menus
const showUserMenu = ref(false)
const showNotifications = ref(false)
const showLogoutModal = ref(false)

// État pour la recherche
const searchQuery = ref('')
const searchResults = ref([])
const showSearchResults = ref(false)
const isSearching = ref(false)
let searchTimeout = null
let hideResultsTimeout = null
let cachedAgents = [] // Cache pour stocker les agents

// Notifications simulées
const notificationCount = ref(0)

// Gestion de la responsivité
const checkScreenSize = () => {
  const width = window.innerWidth
  isMobile.value = width < 768
  isTablet.value = width >= 768 && width < 1024
  
  // Fermer la recherche étendue si on passe en mode desktop
  if (!isMobile.value && isSearchExpanded.value) {
    isSearchExpanded.value = false
  }
}

// Fonction pour basculer le menu mobile (sidebar)
const toggleMobileMenu = () => {
  emit('toggle-sidebar')
}

// Fonction pour basculer la recherche sur mobile
const toggleSearch = async () => {
  isSearchExpanded.value = !isSearchExpanded.value
  if (isSearchExpanded.value) {
    await nextTick()
    searchInput.value?.focus()
  }
}

// Fonction pour vider la recherche
const clearSearch = () => {
  searchQuery.value = ''
  searchResults.value = []
  showSearchResults.value = false
  isSearchExpanded.value = false
}

// Fonction pour basculer les notifications
const toggleNotifications = () => {
  showNotifications.value = !showNotifications.value
}

// Gestion du menu utilisateur
const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

// Fermer le menu utilisateur quand on clique ailleurs
const handleClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showUserMenu.value = false
  }
}

// Rediriger vers la page de profil
const goToProfile = () => {
  showUserMenu.value = false
  router.push('/profile')
}

// Rediriger vers la page de changement de mot de passe
const goToChangePassword = () => {
  showUserMenu.value = false
  router.push('/change-password')
}

// Ouvrir la modale de confirmation de déconnexion
const openLogoutConfirmation = () => {
  showLogoutModal.value = true
  showUserMenu.value = false
}

// Annuler la déconnexion
const cancelLogout = () => {
  showLogoutModal.value = false
}

// Confirmer et effectuer la déconnexion
const confirmLogout = () => {
  authStore.logout()
  router.push('/login')
}

// Informations de l'utilisateur
const userInitial = computed(() => {
  const username = authStore.username || 'U'
  return username.charAt(0).toUpperCase()
})

const userName = computed(() => {
  return authStore.username || 'Utilisateur'
})

const userRole = computed(() => {
  return authStore.role || 'Admin'
})

// Gestion des événements de clic pour le menu déroulant
// Note: onMounted déplacé dans la section de chargement des agents


// Charger les agents au montage du composant
onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', checkScreenSize)
  checkScreenSize() // Vérifier la taille d'écran initiale
  await loadAgents()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', checkScreenSize)
  clearTimeout(searchTimeout)
  clearTimeout(hideResultsTimeout)
})

// Fonction pour charger tous les agents
async function loadAgents() {
  try {
    // Appel à l'API pour récupérer la liste des agents
    const response = await api.get('/api/personnel-liste')
    cachedAgents = response.data.data || []
    
    // Log pour debug
    //console.log('Agents chargés:', cachedAgents.length)
  } catch (error) {
    console.error('Erreur lors du chargement des agents:', error)
    cachedAgents = [] // En cas d'erreur, on initialise avec un tableau vide
  }
}

// Fonction pour rechercher un agent
async function handleSearch() {
  clearTimeout(searchTimeout)
  
  if (searchQuery.value.length < 2) {
    searchResults.value = []
    return
  }
  
  // Afficher l'indicateur de chargement
  isSearching.value = true
  
  // Ajouter un délai avant de lancer la recherche (pour éviter trop d'appels pendant la frappe)
  searchTimeout = setTimeout(() => {
    try {
      // Si le cache est vide (première recherche ou erreur précédente), charger les agents
      if (cachedAgents.length === 0) {
        loadAgents().then(() => performSearch())
      } else {
        performSearch()
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error)
      searchResults.value = []
      isSearching.value = false
    }
  }, 300) // 300ms de délai
}

// Fonction qui effectue la recherche dans le cache d'agents
function performSearch() {
  const query = searchQuery.value.toLowerCase()
  
  // Filtrer les agents selon plusieurs critères
  searchResults.value = cachedAgents
    .filter(agent => {
      return agent.nom?.toLowerCase().includes(query) ||
             agent.prenom?.toLowerCase().includes(query) ||
             agent.matricule?.toLowerCase().includes(query) ||
             agent.fonction?.toLowerCase().includes(query) ||
             agent.grade?.toLowerCase().includes(query) ||
             agent.num_personnel?.toString().includes(query)
    })
    .slice(0, 6) // Limiter à 6 résultats
  
  isSearching.value = false
}

// Fonction pour cacher les résultats avec un délai
function hideResultsDelayed() {
  hideResultsTimeout = setTimeout(() => {
    showSearchResults.value = false;
  }, 200);
}

// Fonction pour naviguer vers le profil d'un agent
function goToAgentProfile(agentId) {
  router.push(`/agent/${agentId}`);
  searchQuery.value = '';
  searchResults.value = [];
  showSearchResults.value = false;
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.header {
  background-color: white;
  color: #333;
  padding: 0 20px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eaeaea;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: calc(100% - 64px); /* Largeur ajustée pour la sidebar fermée */
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 64px; /* Position pour la sidebar fermée */
  z-index: 1001;
  transition: left 0.3s ease, width 0.3s ease;
}

/* Header quand la sidebar est ouverte */
.sidebar-open .header {
  left: 250px; /* Position pour la sidebar ouverte */
  width: calc(100% - 250px); /* Largeur ajustée pour la sidebar ouverte */
}

.header-left {
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--turquoise-very-dark);
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title .material-icons {
  color: var(--turquoise-primary); /* Couleur principale turquoise */
  font-size: 28px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  width: 240px;
}

.search-icon {
  color: #64748b;
  font-size: 20px;
  margin-right: 12px;
  flex-shrink: 0;
}

.search-input {
  border: none;
  background: transparent;
  outline: none;
  color: #333;
  font-size: 14px;
  width: 100%;
  flex: 1;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
}

.search-loading,
.search-no-results {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #666;
  font-size: 14px;
  gap: 8px;
}

.search-no-results .material-icons {
  font-size: 20px;
  color: #999;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-top: 2px solid var(--turquoise-primary); /* Couleur principale turquoise */
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.search-result-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: #f5f5f5;
}

.result-avatar {
  width: 32px;
  height: 32px;
  background-color: var(--turquoise-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  margin-right: 12px;
  font-size: 14px;
}

.result-info {
  flex: 1;
}

.result-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.result-details {
  font-size: 12px;
  color: #666;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.notification-bell {
  position: relative;
  cursor: pointer;
}

.notification-bell .material-icons {
  font-size: 22px;
  color: #555;
}

.badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #f44336;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px;
  border-radius: 24px;
  transition: background-color 0.2s;
  position: relative; /* Ajout pour positionner le dropdown */
}

.user-info:hover {
  background-color: #f5f5f5;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--turquoise-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  margin-right: 10px;
}

.user-details {
  display: flex;
  flex-direction: column;
  margin-right: 5px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.user-role {
  font-size: 12px;
  color: #777;
}

.dropdown-icon {
  color: #777;
  font-size: 20px;
}

/* Styles du sous-menu utilisateur */
.user-dropdown {
  position: absolute;
  top: calc(100% + 5px); /* Position juste en dessous de l'élément parent */
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  width: 220px;
  z-index: 100;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: #f5f7fa;
}

.dropdown-item .material-icons {
  margin-right: 12px;
  font-size: 18px;
  color: #555;
}

/* Styles de la boîte de dialogue de changement de mot de passe */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.password-modal {
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.password-modal h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-form {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #555;
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input input {
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  transition: border-color 0.2s;
}

.password-input input:focus {
  border-color: #1976d2;
  outline: none;
}

.toggle-password {
  position: absolute;
  right: 10px;
  font-size: 20px;
  color: #777;
  cursor: pointer;
  user-select: none;
}

.error-message {
  display: flex;
  align-items: center;
  background-color: #ffebee;
  color: #d32f2f;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
}

.error-message .material-icons {
  margin-right: 8px;
  font-size: 18px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-actions button {
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background-color: transparent;
  color: #555;
  border: 1px solid #ddd;
}

.cancel-btn:hover {
  background-color: #f5f5f5;
}

.submit-btn {
  background-color: #1976d2;
  color: white;
  border: none;
}

.submit-btn:hover {
  background-color: #1565c0;
}

.submit-btn:disabled {
  background-color: #90caf9;
  cursor: not-allowed;
}

/* Styles pour la modale de confirmation de déconnexion */
.confirmation-modal {
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.confirmation-modal h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.confirmation-content {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.warning-icon {
  color: #ff9800;
  font-size: 36px;
  margin-right: 16px;
}

.confirmation-content p {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.logout-btn {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
}

.logout-btn:hover {
  background-color: #d32f2f;
}

.logout-btn .material-icons {
  font-size: 18px;
  margin-right: 8px;
}

/* Spinner pour le chargement */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* === STYLES RESPONSIVE === */

/* Bouton menu */
.menu-btn {
  background: none;
  border: none;
  color: var(--turquoise-primary);
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-btn:hover {
  background-color: #f0f9ff;
  transform: scale(1.1);
}

/* Header center pour la recherche */
.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
}

/* Styles pour la recherche responsive */
.search-box {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  transition: all 0.3s ease;
}

.search-input-container {
  display: flex;
  align-items: center;
  background-color: #f8fafc;
  border-radius: 24px;
  padding: 8px 16px;
  width: 100%;
  transition: all 0.3s ease;
  border: none;
  outline: none;
}

.search-input-container:focus-within {
  background-color: white;
  box-shadow: 0 0 0 2px rgba(32, 211, 208, 0.2);
}

.search-toggle {
  display: none;
  background: none;
  border: none;
  color: var(--turquoise-primary);
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-right: 8px;
}

.search-toggle:hover {
  background-color: #f0f9ff;
}

.search-clear {
  background: none;
  border: none;
  color: #64748b;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.search-clear:hover {
  background-color: #f1f5f9;
  color: #ef4444;
}

/* Styles pour les notifications améliorées */
.notification-bell {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.notification-bell:hover {
  background-color: #f8fafc;
  transform: scale(1.05);
}

.notification-bell .material-icons {
  font-size: 22px;
  color: #64748b;
  transition: color 0.2s ease;
}

.notification-bell:hover .material-icons {
  color: var(--turquoise-primary);
}

/* Améliorations du dropdown utilisateur */
.dropdown-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, var(--turquoise-primary), var(--turquoise-dark));
  color: white;
}

.dropdown-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  margin-right: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.dropdown-user-info {
  flex: 1;
}

.dropdown-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
}

.dropdown-role {
  font-size: 13px;
  opacity: 0.9;
}

.dropdown-divider {
  height: 1px;
  background-color: #e2e8f0;
  margin: 0;
}

/* Modal améliorée */
.modal-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  margin-left: 12px;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.warning-icon {
  color: #f59e0b;
  font-size: 28px;
}

.confirmation-content p {
  margin: 0;
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.modal-actions button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.cancel-btn {
  background-color: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0 !important;
}

.cancel-btn:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1 !important;
}

.logout-btn {
  background-color: #ef4444;
  color: white;
}

.logout-btn:hover {
  background-color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Classes utilitaires pour le responsive */
.mobile-hidden {
  display: none;
}

/* === BREAKPOINTS RESPONSIVE === */

/* Tablettes (768px - 1023px) */
@media (max-width: 1023px) {
  .header {
    padding: 0 16px;
  }
  
  .page-title {
    font-size: 1.3rem;
  }
  
  .page-title .material-icons {
    font-size: 24px;
  }
  
  .search-box {
    max-width: 300px;
  }
  
  .header-actions {
    gap: 12px;
  }
}

/* Mobile (max-width: 767px) */
@media (max-width: 767px) {
  .header {
    padding: 0 12px;
    height: 60px;
    position: fixed;
    left: 0; /* Position par défaut */
    width: 100%; /* Largeur par défaut */
    transition: left 0.3s ease, width 0.3s ease; /* Ajout de transitions */
    z-index: 1003; /* Plus élevé que la sidebar pour garder le bouton menu visible */
  }
  
  /* Header quand la sidebar est ouverte sur mobile - pas de déplacement */
  .sidebar-open .header {
    left: 0 !important; /* Reste en position normale */
    width: 100% !important; /* Garde sa largeur normale */
  }
  
  
  .header-left {
    flex: 1;
  }
  
  .page-title {
    font-size: 1.1rem;
    gap: 8px;
  }
  
  .title-text {
    display: none;
  }
  
  .page-title .material-icons {
    font-size: 24px;
  }
  
  .header-center {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px;
    z-index: 50;
    max-width: none;
    margin: 0;
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }
  
  .search-expanded .header-center {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }
  
  .search-toggle {
    display: flex;
  }
  
  .search-box {
    width: 100%;
    max-width: none;
  }
  
  .mobile-hidden {
    display: none;
  }
  
  .search-input-container {
    border-radius: 12px;
    padding: 12px 16px;
    background-color: #f1f5f9;
  }
  
  .search-input {
    font-size: 16px; /* Évite le zoom sur iOS */
  }
  
  .user-details {
    display: none;
  }
  
  .header-actions {
    gap: 8px;
  }
  
  .avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
    margin-right: 0;
  }
  
  .notification-bell .material-icons {
    font-size: 20px;
  }
  
  .dropdown-icon {
    display: none;
  }
  
  /* Modal responsive */
  .confirmation-modal {
    margin: 12px;
    max-width: calc(100vw - 24px);
    padding: 20px;
  }
  
  .modal-header h3 {
    font-size: 18px;
  }
  
  .modal-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  
  .modal-actions button {
    width: 100%;
    justify-content: center;
    padding: 12px;
  }
  
  /* Dropdown utilisateur mobile */
  .user-dropdown {
    right: -12px;
    width: 280px;
    max-width: calc(100vw - 24px);
  }
}

/* Très petits écrans (max-width: 479px) */
@media (max-width: 479px) {
  .header {
    padding: 0 8px;
  }
  
  .page-title {
    font-size: 1rem;
  }
  
  .search-input-container {
    padding: 10px 14px;
    background-color: #f1f5f9;
  }
  
  .avatar {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }
  
  .notification-bell .material-icons {
    font-size: 18px;
  }
}

/* Écrans larges (min-width: 1200px) */
@media (min-width: 1200px) {
  .header {
    padding: 0 32px;
  }
  
  .header-center {
    max-width: 500px;
  }
  
  .search-box {
    max-width: 450px;
  }
  
  .search-input-container {
    padding: 10px 20px;
    background-color: #f8fafc;
  }
  
  .header-actions {
    gap: 20px;
  }
}

/* Mode sombre (préparation pour future implémentation) */
@media (prefers-color-scheme: dark) {
  .header {
    background-color: #1e293b;
    color: #f1f5f9;
    border-bottom-color: #334155;
  }
  
  .search-input-container {
    background-color: #334155;
    border-color: #475569;
    color: #f1f5f9;
  }
  
  .search-input {
    color: #f1f5f9;
  }
  
  .search-input::placeholder {
    color: #94a3b8;
  }
}

/* Animations et transitions fluides */
.header * {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Focus visible pour l'accessibilité */
.header button:focus-visible,
.search-input:focus-visible {
  outline: 2px solid var(--turquoise-primary);
  outline-offset: 2px;
}

/* États de hover améliorés */
@media (hover: hover) {
  .search-result-item:hover {
    background-color: #f8fafc;
    transform: translateX(4px);
  }
  
  .dropdown-item:hover {
    background-color: #f8fafc;
    transform: translateX(4px);
  }
}
</style>